import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import React from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import { BatchesQuery_batches_edges_node } from "../../types/BatchesQuery";
import {
  formatDate,
  formatDateWithTime,
  getBatchStatusValue
} from "../../utils/handlers";

const useStyles = makeStyles(
  () => ({
    tableRow: {
      "&:hover": {
        backgroundColor: "#D5D8DC"
      },
      cursor: "pointer"
    }
  }),
  { name: "BatchList" }
);

function BatchList(props) {
  const {
    disabled,
    settings,
    batches,
    pageInfo,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    isChecked,
    selected,
    toggle,
    toggleAll
  } = props;
  const navigate = useNavigate();
  const classes = useStyles(props);
  const numberOfColumns = 7;

  return (
    <ResponsiveTable>
      <TableHead
        colSpan={numberOfColumns}
        selected={selected}
        disabled={disabled}
        items={batches}
        toggleAll={toggleAll}
      >
        <TableCellHeader>
          <FormattedMessage defaultMessage="Batch ID" />
        </TableCellHeader>
        <TableCellHeader>
          <FormattedMessage defaultMessage="Mall" />
        </TableCellHeader>
        <TableCellHeader>
          <FormattedMessage defaultMessage="Date Generated" />
        </TableCellHeader>
        <TableCellHeader>
          <FormattedMessage defaultMessage="Period Covered" />
        </TableCellHeader>
        <TableCellHeader>
          <FormattedMessage defaultMessage="Settlement Date" />
        </TableCellHeader>
        <TableCellHeader>
          <FormattedMessage defaultMessage="Batch Status" />
        </TableCellHeader>
      </TableHead>
      <TableBody>
        {batches &&
          renderCollection(
            batches,
            (batch: BatchesQuery_batches_edges_node) => {
              const isSelected = batch ? isChecked(batch?.id) : false;
              return (
                <TableRow
                  className={classes.tableRow}
                  key={batch ? batch.id : "skeleton"}
                  selected={isSelected}
                  onClick={() => navigate(`/payout/batch/${atob(batch.id)}`)}
                >
                  <TableCell
                    padding="checkbox"
                    onClick={e => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={isSelected}
                      disabled={disabled}
                      onChange={() => toggle(batch.id)}
                    />
                  </TableCell>
                  <TableCell>
                    {maybe(() => batch.id, null) ? (
                      `${atob(batch.id)}`
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe(() => batch.mall_name, null) ? (
                      batch.mall_name.toUpperCase()
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe(() => batch.generated_at, null) ? (
                      formatDateWithTime(batch.generated_at)
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe(() => batch.coverage_start_date, null) ? (
                      formatDate(batch.coverage_start_date) +
                      " - " +
                      formatDate(batch.coverage_end_date)
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe(() => batch.credit_date, null) ? (
                      formatDate(batch.credit_date)
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell>
                    {maybe(() => batch.status, null) ? (
                      getBatchStatusValue(batch.status)
                    ) : (
                      <Skeleton />
                    )}
                  </TableCell>
                </TableRow>
              );
            },
            () => (
              <TableRow>
                <TableCell colSpan={numberOfColumns}>
                  <FormattedMessage defaultMessage="No batches found" />
                </TableCell>
              </TableRow>
            )
          )}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            settings={settings}
            hasNextPage={
              pageInfo && !disabled ? pageInfo.hasNextPage : undefined
            }
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : undefined
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
    </ResponsiveTable>
  );
}

export default BatchList;
